import React, { useEffect, useState } from 'react';
import { api } from '../../shared/api';
import { formatDate } from '../../shared/formatting';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.86rem', borderBottom: '1px solid #f3ecdb' };

type Overview = {
    total_headcount: number;
    headcount_by_company: { company_id: number | null; company_name: string | null; cnt: number }[];
    headcount_by_department: { department_id: number | null; department_name: string | null; cnt: number }[];
    status_breakdown: { status: string; cnt: number }[];
    leave_usage: { rows_cnt?: number; entitled_days?: number; used_days?: number; remaining_days?: number }[];
    document_expiry: { id: number; employee_id: number; employee_name: string; expires_at: string; doc_type?: string; title?: string }[];
    stimulation_summary: {
        company_id: number | null; company_name: string | null; fiscal_year: number; fiscal_month: number;
        employees: number; total_turnover: number; total_ruc: number; total_stim: number; adjusted_stim: number;
    }[];
};

const eur = (v: number | string | null | undefined) =>
    (Number(v ?? 0)).toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const STATUS_LABEL: Record<string, { hr: string; en: string }> = {
    active: { hr: 'Aktivni', en: 'Active' },
    on_leave: { hr: 'Na dopustu', en: 'On leave' },
    terminated: { hr: 'Prekinuti', en: 'Terminated' },
};

export function AnalyticsTab({ token, locale }: { token: string; locale: Locale; user: User }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Overview | null>(null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setData(null);
        setErr(null);
        api<Overview>('/hr/analytics', token)
            .then((r) => { if (alive) setData(r); })
            .catch((e) => { if (alive) setErr(String(e?.message ?? e)); });
        return () => { alive = false; };
    }, [token]);

    if (err) return <div style={{ ...card, color: '#b3261e' }}>⚠ {err}</div>;
    if (!data) return <div style={{ ...card, color: BROWN }}>{t('Učitavam analitiku…', 'Loading analytics…')}</div>;

    const tile = (label: string, value: React.ReactNode, accent = GOLD): React.ReactNode => (
        <div style={{ ...card, padding: 14, minWidth: 150, borderLeft: `4px solid ${accent}` }}>
            <div className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 700, color: BROWN }}>{value}</div>
        </div>
    );

    const maxCompanyCnt = Math.max(1, ...data.headcount_by_company.map((r) => r.cnt));
    const leave = data.leave_usage?.[0];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Tiles */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {tile(t('Ukupno zaposlenika', 'Total headcount'), data.total_headcount)}
                {data.status_breakdown.map((s) =>
                    <React.Fragment key={s.status}>
                        {tile(t(STATUS_LABEL[s.status]?.hr ?? s.status, STATUS_LABEL[s.status]?.en ?? s.status), s.cnt, s.status === 'active' ? '#2e9e5b' : s.status === 'terminated' ? '#b3261e' : GOLD)}
                    </React.Fragment>
                )}
            </div>

            {/* Headcount po firmi (bar) */}
            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Zaposlenici po firmi', 'Headcount by company')}</strong>
                {data.headcount_by_company.length === 0 ? (
                    <div className="muted" style={{ marginTop: 8 }}>{t('Nema podataka.', 'No data.')}</div>
                ) : (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {data.headcount_by_company.map((r) => (
                            <div key={String(r.company_id)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 160, fontSize: '0.84rem', color: BROWN }}>{r.company_name ?? '—'}</div>
                                <div style={{ flex: 1, background: '#f3ecdb', borderRadius: 6, overflow: 'hidden' }}>
                                    <div style={{ width: `${(r.cnt / maxCompanyCnt) * 100}%`, minWidth: 2, height: 18, background: GOLD }} />
                                </div>
                                <div style={{ width: 36, textAlign: 'right', fontWeight: 600, color: BROWN }}>{r.cnt}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Headcount po odjelu */}
            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Zaposlenici po poslovnici/odjelu', 'Headcount by department')}</strong>
                {data.headcount_by_department.length === 0 ? (
                    <div className="muted" style={{ marginTop: 8 }}>{t('Nema podataka.', 'No data.')}</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                        <thead><tr><th style={th}>{t('Poslovnica / odjel', 'Department')}</th><th style={{ ...th, textAlign: 'right' }}>{t('Broj', 'Count')}</th></tr></thead>
                        <tbody>
                            {data.headcount_by_department.map((r) => (
                                <tr key={String(r.department_id)}>
                                    <td style={td}>{r.department_name ?? '—'}</td>
                                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{r.cnt}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Leave usage (ako tablica postoji) */}
            {leave && (leave.entitled_days != null || leave.used_days != null || leave.remaining_days != null) ? (
                <div style={card}>
                    <strong style={{ color: BROWN }}>{t('Iskorištenje godišnjeg odmora', 'Leave usage')}</strong>
                    <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                        {leave.entitled_days != null ? tile(t('Pravo (dani)', 'Entitled (days)'), Number(leave.entitled_days).toLocaleString('hr-HR')) : null}
                        {leave.used_days != null ? tile(t('Iskorišteno', 'Used'), Number(leave.used_days).toLocaleString('hr-HR')) : null}
                        {leave.remaining_days != null ? tile(t('Preostalo', 'Remaining'), Number(leave.remaining_days).toLocaleString('hr-HR'), '#2e9e5b') : null}
                    </div>
                </div>
            ) : null}

            {/* Document expiry alerts */}
            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Istek dokumenata (60 dana)', 'Document expiry (60 days)')}</strong>
                {data.document_expiry.length === 0 ? (
                    <div className="muted" style={{ marginTop: 8 }}>{t('Nema dokumenata pred istekom.', 'No documents expiring soon.')}</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                        <thead><tr>
                            <th style={th}>{t('Zaposlenik', 'Employee')}</th>
                            <th style={th}>{t('Dokument', 'Document')}</th>
                            <th style={th}>{t('Istek', 'Expires')}</th>
                        </tr></thead>
                        <tbody>
                            {data.document_expiry.map((d) => (
                                <tr key={d.id}>
                                    <td style={td}>{d.employee_name || '—'}</td>
                                    <td style={td}>{d.title ?? d.doc_type ?? '—'}</td>
                                    <td style={{ ...td, color: '#b3261e', fontWeight: 600 }}>{d.expires_at ? formatDate(d.expires_at, locale) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Stimulacija ↔ promet */}
            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Stimulacija ↔ promet (po periodu)', 'Stimulation ↔ turnover (by period)')}</strong>
                {data.stimulation_summary.length === 0 ? (
                    <div className="muted" style={{ marginTop: 8 }}>{t('Nema unesenih stimulacija.', 'No stimulation entries.')}</div>
                ) : (
                    <div style={{ overflowX: 'auto', marginTop: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                            <thead><tr>
                                <th style={th}>{t('Firma', 'Company')}</th>
                                <th style={th}>{t('Period', 'Period')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Djelatnika', 'Employees')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Promet', 'Turnover')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>RUC</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Stimulacija', 'Stimulation')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Korigirano', 'Adjusted')}</th>
                            </tr></thead>
                            <tbody>
                                {data.stimulation_summary.map((r, i) => (
                                    <tr key={i}>
                                        <td style={td}>{r.company_name ?? '—'}</td>
                                        <td style={td}>{String(r.fiscal_month).padStart(2, '0')}.{r.fiscal_year}.</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{r.employees}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{eur(r.total_turnover)}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{eur(r.total_ruc)}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{eur(r.total_stim)}</td>
                                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{eur(r.adjusted_stim)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
